import React, { useState, useEffect, useCallback } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Badge } from './ui/Badge';
import { useToast } from './Toast';
import { fetchSessions, deleteSession } from '../api';

const DEVICE_ICONS = {
  Phone: 'bi-phone',
  Tablet: 'bi-tablet',
  Desktop: 'bi-display',
};

const SessionsModal = ({ onClose, onClearAuth }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const toast = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSessions();
      setSessions(data.sessions || []);
    } catch {
      setSessions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const formatTime = (iso) => {
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleDelete = async (s) => {
    setDeleting(s.id);
    try {
      await deleteSession(s.id);
      setSessions((p) => p.filter((x) => x.id !== s.id));
      if (s.is_current && onClearAuth) onClearAuth();
    } catch {
      toast('Не удалось завершить сессию', 'error');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Активные сессии"
      subtitle="Устройства, на которых выполнен вход"
      icon="bi-shield-lock"
      size="md"
    >
      {loading ? (
        <div className="space-y-md" aria-hidden="true">
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton h-[72px] w-full rounded-md" />
          ))}
        </div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-3xl">
          <i className="bi bi-shield-slash text-2xl text-text-muted" aria-hidden="true" />
          <p className="text-body-sm text-text-muted mt-md">Нет активных сессий</p>
        </div>
      ) : (
        <ul className="space-y-md list-none p-0 m-0">
          {sessions.map((s) => (
            <li
              key={s.id}
              className="flex items-start gap-lg p-lg rounded-md border border-border bg-surface-sunken"
            >
              <div className="w-9 h-9 rounded-md bg-primary-soft text-primary flex items-center justify-center shrink-0">
                <i className={`bi ${DEVICE_ICONS[s.device] || 'bi-display'}`} aria-hidden="true" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-md flex-wrap">
                  <span className="text-body-sm font-medium text-text-strong">{s.browser}</span>
                  <span className="text-body-xs text-text-muted">{s.os}</span>
                  {s.is_current && (
                    <Badge variant="success" icon="bi-check-circle">Текущая</Badge>
                  )}
                </div>
                <p className="font-mono tabular text-body-xs text-text-muted truncate mt-xs">{s.ip}</p>
                <div className="flex gap-lg mt-xs flex-wrap">
                  <span className="font-mono tabular text-body-xs text-text-muted">
                    <i className="bi bi-box-arrow-in-right mr-sm" aria-hidden="true" />
                    {formatTime(s.created)}
                  </span>
                  <span className="font-mono tabular text-body-xs text-text-muted">
                    <i className="bi bi-activity mr-sm" aria-hidden="true" />
                    {formatTime(s.last_seen)}
                  </span>
                </div>
              </div>

              <Button
                variant="danger"
                size="sm"
                icon="bi-box-arrow-right"
                loading={deleting === s.id}
                onClick={() => handleDelete(s)}
                className="shrink-0"
                title="Завершить сессию"
              >
                Завершить
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
};

export default SessionsModal;
